import { isFeatureEnabled } from '../config/feature-flags';

/**
 * Period format constants
 */
export const MFR_DATE_FORMAT = 'YYYYMM';
export const NETPPM_DATE_FORMAT = 'YYYY-MM-DD';
export const QTD_SUFFIX = ' QTD';
export const YTD_SUFFIX = ' YTD';
export const WEEK_PREFIX = 'week-';

/**
 * Start year constants for period generation
 */
export const MFR_START_YEAR = 2023;
export const NETPPM_START_YEAR = 2024;

/**
 * Day of month when previous month's data becomes available (month close day)
 */
export const MONTH_CLOSE_CALENDAR_DAY = 7;

const getMonthName = (month: number): string => 
    new Date(2000, month - 1).toLocaleString('en-US', { month: 'short' });

/**
 * Formats month value based on the provided format string
 * Supports: MFR_DATE_FORMAT (YYYYMM) or NETPPM_DATE_FORMAT (YYYY-MM-DD - last day of month)
 */
const formatMonthValue = (year: number, month: number, format: string): string => {
    const monthStr = month.toString().padStart(2, '0');
    if (format === NETPPM_DATE_FORMAT) {
        // Get last day of month
        const lastDay = new Date(year, month, 0).getDate();
        return `${year}-${monthStr}-${lastDay.toString().padStart(2, '0')}`;
    }
    if (format === MFR_DATE_FORMAT) {
        return `${year}${monthStr}`;
    }
    throw new Error(`Unknown period format: ${format}. Supported formats are: ${MFR_DATE_FORMAT}, ${NETPPM_DATE_FORMAT}`);
};

const generateMonthData = (year: number, month: number, format: string, includeWeeks: boolean) => {
    const base = {
        title: `${getMonthName(month)} ${year}`,
        value: formatMonthValue(year, month, format)
    };
    
    if (!includeWeeks) return { ...base, isLeaf: true };
    
    const lastAvailableSaturday = getLastAvailableSaturday();
    const saturdays = getSaturdaysInMonth(year, month).filter(s => s <= lastAvailableSaturday);
    
    if (saturdays.length === 0) return { ...base, isLeaf: true };
    
    return {
        ...base,
        isLeaf: false,
        children: saturdays.map((s, i) => generateWeekData(s, i + 1)).reverse()
    };
};

const getQuarterValue = (year: number, quarter: number, monthLimit: number | undefined, quarterEndMonth: number): string => {
    if (isFeatureEnabled('Q4_2025_PERIOD_OVERRIDE') && year === 2025 && quarter === 4) {
        return '202511 QTD';
    }
    if (!monthLimit || monthLimit >= quarterEndMonth) {
        return `Q${quarter}-${year.toString().slice(-2)}`;
    }
    return `${year}${monthLimit.toString().padStart(2, '0')}${QTD_SUFFIX}`;
};

interface NodeConfig {
    selectable?: boolean;
}

export interface GeneratePeriodsOptions {
    yearConfig?: NodeConfig;
    quarterConfig?: NodeConfig;
    monthConfig?: NodeConfig;
    weekConfig?: NodeConfig;
}

const DEFAULT_NODE_CONFIG: NodeConfig = { selectable: true };

// Get last Saturday with available data (Saturday + 2 days = Monday)
const getLastAvailableSaturday = (): Date => {
    const today = new Date();
    // Go back 2 days for data availability delay
    const adjusted = new Date(Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate() - 2
    ));
    // Roll back to most recent Saturday (day 6)
    const daysSinceSaturday = (adjusted.getUTCDay() + 1) % 7;
    adjusted.setUTCDate(adjusted.getUTCDate() - daysSinceSaturday);
    return adjusted;
};

// Get last month with available data (7-day delay after month end)
const getLastAvailableMonth = (): Date => {
    const today = new Date();
    const monthsToSubtract = today.getUTCDate() >= MONTH_CLOSE_CALENDAR_DAY ? 1 : 2;
    return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - monthsToSubtract));
};

// Get all Saturdays in a month (UTC)
const getSaturdaysInMonth = (year: number, month: number): Date[] => {
    const saturdays: Date[] = [];
    const date = new Date(Date.UTC(year, month - 1, 1));
    while (date.getUTCDay() !== 6) date.setUTCDate(date.getUTCDate() + 1);
    while (date.getUTCMonth() === month - 1) {
        saturdays.push(new Date(date));
        date.setUTCDate(date.getUTCDate() + 7);
    }
    return saturdays;
};

// Format week value as YYYY-MM-DD (UTC)
const formatWeekValue = (saturday: Date): string => {
    const y = saturday.getUTCFullYear();
    const m = (saturday.getUTCMonth() + 1).toString().padStart(2, '0');
    const d = saturday.getUTCDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Generate week node
const generateWeekData = (saturday: Date, weekNum: number) => {
    const dateStr = formatWeekValue(saturday);
    return {
        title: `Week ${weekNum} (${dateStr})`,
        value: `${WEEK_PREFIX}${dateStr}`,
        isLeaf: true,
        selectable: true
    };
};

const generateQuarterData = (year: number, quarter: number, monthLimit: number | undefined, format: string, includeWeeks: boolean) => {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    
    // Calculate which months to include based on limit
    const lastMonthToInclude = monthLimit && monthLimit >= startMonth 
                        ? Math.min(endMonth, monthLimit)
                        : endMonth;

    const children = [];
    
    // Generate months in descending order (latest first)
    for (let monthNumber = lastMonthToInclude; monthNumber >= startMonth; monthNumber--) {
        children.push(generateMonthData(year, monthNumber, format, includeWeeks));
    }

    return {
        title: `Q${quarter} ${year}`,
        value: getQuarterValue(year, quarter, monthLimit, endMonth),
        isLeaf: false,
        selectable: true,
        children
    };
};

const getYearValue = (year: number, monthLimit: number | undefined): string => {
    if (isFeatureEnabled('Q4_2025_PERIOD_OVERRIDE') && year === 2025) {
        return '202511 YTD';
    }
    return !monthLimit || monthLimit >= 12
        ? `FY${year.toString().slice(-2)}`
        : `${year}${monthLimit.toString().padStart(2, '0')}${YTD_SUFFIX}`;
};

const generateYearData = (year: number, monthLimit: number | undefined, format: string, includeWeeks: boolean) => {
    const maxQuarter = monthLimit ? Math.ceil(monthLimit / 3) : 4;
    const quarters = [];
    
    // Generate quarters in descending order (Q4 -> Q1)
    for (let quarterNumber = maxQuarter; quarterNumber >= 1; quarterNumber--) {
        quarters.push(generateQuarterData(year, quarterNumber, monthLimit, format, includeWeeks));
    }

    return {
        title: year.toString(),
        value: getYearValue(year, monthLimit),
        isLeaf: false,
        selectable: true,
        children: quarters
    };
};

const applyConfig = (node: any, config: Record<string, NodeConfig>, level?: string): any => {
    const levelConfig = level ? config[level] : undefined;
    const childLevel = level === undefined ? 'year' :
                       level === 'year' ? 'quarter' :
                       level === 'quarter' ? 'month' :
                       level === 'month' ? 'week' : undefined;

    return {
        ...node,
        selectable: levelConfig?.selectable ?? node.selectable,
        children: node.children?.map((c: any) => applyConfig(c, config, childLevel))
    };
};

export const generatePeriods = (startYear: number, format: string, options: GeneratePeriodsOptions = {}) => {
    const config = {
        year: { ...DEFAULT_NODE_CONFIG, ...options.yearConfig },
        quarter: { ...DEFAULT_NODE_CONFIG, ...options.quarterConfig },
        month: { ...DEFAULT_NODE_CONFIG, ...options.monthConfig },
        week: { ...DEFAULT_NODE_CONFIG, ...options.weekConfig },
    };

    // Validate format
    const validFormats = [MFR_DATE_FORMAT, NETPPM_DATE_FORMAT];
    if (!validFormats.includes(format)) {
        throw new Error(`Invalid period format: ${format}. Supported formats are: ${validFormats.join(', ')}`);
    }

    // Weekly: 2-day delay (data available Monday after Saturday)
    // Monthly: 7-day delay (MONTH_CLOSE_CALENDAR_DAY)
    const includeWeeks = !!options.weekConfig;

    const lastAvailableDate = includeWeeks ? getLastAvailableSaturday() : getLastAvailableMonth();
    const lastCompleteMonth = lastAvailableDate.getUTCMonth() + 1;
    const endYear = lastAvailableDate.getUTCFullYear();

    const yearChildren = [];
    for (let year = endYear; year >= startYear; year--) {
        if (year === endYear) {
            // Only include current year if there are completed months
            if (lastCompleteMonth > 0) {
                yearChildren.push(generateYearData(year, lastCompleteMonth, format, includeWeeks));
            }
        } else {
            yearChildren.push(generateYearData(year, undefined, format, includeWeeks));
        }
    }

    const periods = {
        value: "periods",
        title: "Period",
        children: yearChildren
    };

    return applyConfig(periods, config);
};

/**
 * Finds the first selectable (leaf) period in a tree structure
 * Traverses depth-first to find the first leaf node (most recent period)
 */
export const getFirstSelectablePeriod = (treeData: any[]): string | undefined => {
    for (const node of treeData) {
        if (node.children) {
            const result = getFirstSelectablePeriod(node.children);
            if (result) return result;
        } else if (node.isLeaf) {
            return node.value;
        }
    }
    return undefined;
};

/**
 * Maps a period value to its current tree equivalent by matching titles.
 * Handles QTD/YTD → complete format conversion when period is now complete.
 */
export const findCurrentPeriodValue = (treeData: any[], value: string): string => {
    // Check if value exists in tree
    const findValue = (nodes: any[]): boolean => {
        for (const node of nodes) {
            if (node.value === value) return true;
            if (node.children && findValue(node.children)) return true;
        }
        return false;
    };
    if (findValue(treeData)) return value;

    // Derive title from value and find matching node
    const title = getPeriodTitle(value);
    if (!title) return value;

    const findByTitle = (nodes: any[], isLeaf?: boolean): string | null => {
        for (const node of nodes) {
            if (node.title === title && (isLeaf === undefined || node.isLeaf === isLeaf)) return node.value;
            if (node.children) {
                const found = findByTitle(node.children, isLeaf);
                if (found) return found;
            }
        }
        return null;
    };

    // Monthly values need isLeaf check to avoid matching year nodes
    const isMonthly = /^\d{6}$/.test(value);
    return findByTitle(treeData, isMonthly ? true : undefined) || value;
};

/**
 * Derives display title from any period value format.
 */
export const getPeriodTitle = (value: string): string | null => {
    // Monthly: YYYYMM → "Jan 2024"
    let match = value.match(/^(\d{4})(\d{2})$/);
    if (match) return `${getMonthName(parseInt(match[2]))} ${match[1]}`;

    // QTD: YYYYMM QTD → "Q1 2024"
    match = value.match(/^(\d{4})(\d{2}) QTD$/);
    if (match) return `Q${Math.ceil(parseInt(match[2]) / 3)} ${match[1]}`;

    // Quarterly: Q1-YY → "Q1 2024"
    match = value.match(/^Q([1-4])-(\d{2})$/);
    if (match) return `Q${match[1]} 20${match[2]}`;

    // YTD: YYYYMM YTD → "2024"
    match = value.match(/^(\d{4})\d{2} YTD$/);
    if (match) return match[1];

    // Annual: FYYY → "2024"
    match = value.match(/^FY(\d{2})$/);
    if (match) return `20${match[1]}`;

    return null;
};
